import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Clock, Upload, CheckCircle, AlertTriangle, ChevronDown, ChevronLeft, ChevronRight, Download, FolderArchive, Loader2, Ban, RotateCcw, Trash2, MoreHorizontal } from 'lucide-react';
import { apiDelete, apiGet, apiPost } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmtTime, fmtBytes } from '@/lib/format';
import { cn, order } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { DataTable, type Column } from '@/components/DataTable';
import { BulkBar, RowCheckbox } from '@/components/BulkBar';
import { Drawer } from '@/components/Drawer';
import { evName, mediaName, mediaVariant, cmdVariant } from '@/lib/labels';
import { transferPercent, syncCommandStatus, syncStageLabel } from '@/lib/media-presentation';
import { eventDateBounds } from '@/lib/eventSearch';
import type { Device, MediaListItem, MediaListResponse } from '@/types';

const PAGE_SIZE = 40;
const STATUS_FILTERS = [
  { label: '全部檔案', value: '' },
  { label: '待完成傳輸', value: 'in_flight' },
  { label: '待上傳', value: 'pending' },
  { label: '上傳中', value: 'uploading' },
  { label: '已接收', value: 'uploaded' },
  { label: '驗證中', value: 'verifying' },
  { label: '已保存', value: 'stored' },
  { label: '失敗', value: 'failed' },
  { label: '取消中', value: 'cancelling' },
  { label: '已取消', value: 'cancelled' },
  { label: '刪除中', value: 'deleting' },
  { label: '刪除失敗', value: 'delete_failed' },
  { label: '已刪除紀錄', value: 'deleted' },
];
const METRICS = [
  { key: 'pending', label: '等待上傳', Icon: Clock },
  { key: 'uploading', label: '傳輸未完成', Icon: Upload },
  { key: 'stored', label: '已安全保存', Icon: CheckCircle },
  { key: 'failed', label: '傳輸失敗', Icon: AlertTriangle },
] as const;

/** 百分比只來自實際位元組；未知大小不顯示虛構進度。
 *  live：裝置在線且還在傳（0% 到 100% 之間）才加斜紋；離線時靜止，配合「傳輸未完成」的標示。 */
function FileProgress({ expected, actual, label, live = false }: {
  expected?: number | null; actual?: number | null; label: string; live?: boolean;
}) {
  const percent = transferPercent(expected, actual);
  if (percent === null) return null;
  return (
    <div className="flex min-w-0 items-center gap-2">
      <div role="progressbar" aria-label={label} aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}
        className="h-1.5 min-w-8 flex-1 overflow-hidden rounded-full bg-secondary">
        <div className={cn('h-full rounded-full bg-primary transition-[width] duration-300',
                           live && percent > 0 && percent < 100 && 'progress-live')}
          style={{ width: `${percent}%` }} />
      </div>
      <span className="w-10 shrink-0 text-right font-mono text-xs text-muted-foreground">{percent}%</span>
    </div>
  );
}

interface SyncProgress {
  active: boolean;
  command_id?: string;
  command_state?: string;
  created_at?: string;
  expired?: boolean;
  scan?: Record<string, unknown>;
  media?: Record<string, number>;
  uploading_files?: Array<{
    filename: string; expected_bytes: number | null; actual_bytes: number | null;
    status: string; updated_at: string | null;
  }>;
}

function SyncProgressCard({ deviceId }: { deviceId: string }) {
  const { data, error } = useQuery<SyncProgress>({
    queryKey: ['sync-progress', deviceId],
    queryFn: () => apiGet(`/api/v1/devices/${encodeURIComponent(deviceId)}/sync-progress`),
    refetchInterval: 3_000,
  });
  if (error) return <p role="status" className="text-sm text-destructive">補傳狀態暫時無法更新，檔案狀態請以下方清單為準。</p>;
  if (!data || (!data.active && !data.command_id)) return null;
  const scan = data.scan ?? {};
  const files = data.uploading_files ?? [];
  const command = syncCommandStatus(data.command_state, data.expired);
  return (
    <details className="sync-disclosure group rounded-md border border-border bg-card">
      <summary className="flex cursor-pointer list-none flex-wrap items-center gap-x-3 gap-y-2 rounded-md p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:px-5">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-sm bg-accent text-primary"><Upload className="h-5 w-5" /></span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold">SD 補傳追蹤 <span className="ml-1 break-all font-mono text-xs font-normal text-muted-foreground">{deviceId}</span></span>
          <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">{files.length ? `最近回報 ${files.length} 筆傳輸紀錄` : '目前沒有傳輸中的檔案'} · 展開查看</span>
        </span>
        <Badge variant={cmdVariant(command.state)}>{command.label}</Badge>
        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
      </summary>
      <div className="space-y-4 border-t border-border p-4 sm:p-5">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
          <span>已掃描 <strong className="font-mono">{Number(scan.scanned) || 0}</strong></span>
          <span>已保存略過 <strong className="font-mono">{Number(scan.skipped) || 0}</strong></span>
          {typeof scan.stage === 'string' && <span className="text-muted-foreground">最近回報：{syncStageLabel(scan.stage)}</span>}
        </div>
        <p className="text-xs leading-relaxed text-muted-foreground">
          {data.created_at && <>命令建立於 {fmtTime(data.created_at)}。 </>}
          {command.state === 'timed_out'
            ? '命令回覆已逾時，檔案可能仍在傳輸；請以下方各檔案的狀態及更新時間確認進度。'
            : '命令回覆與檔案保存分開追蹤；命令完成不代表所有檔案已保存。'}
        </p>
        {command.state === 'failed' && !!scan.message && <p className="break-words text-sm text-destructive">{String(scan.message)}</p>}
        {!!files.length && <ul className="max-h-72 divide-y divide-border overflow-y-auto rounded-sm border border-border px-3">
          {files.map((f) => <li key={f.filename} className="grid gap-2 py-3 sm:grid-cols-[minmax(0,1fr)_160px] sm:items-center sm:gap-4">
            <div className="min-w-0">
              <p className="break-all font-mono text-xs">{f.filename}</p>
              <p className="mt-1 text-xs text-muted-foreground">最後更新 {fmtTime(f.updated_at)}</p>
            </div>
            <div className="space-y-2">
              <Badge variant={mediaVariant(f.status)}>{mediaName(f.status)}</Badge>
              {f.status === 'uploading' && <FileProgress live expected={f.expected_bytes} actual={f.actual_bytes} label={`${f.filename} 上傳進度`} />}
            </div>
          </li>)}
        </ul>}
      </div>
    </details>
  );
}

export default function MediaPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const fileList = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get('status') ?? '';
  const selectedDevice = searchParams.get('device') ?? '';
  const filenameFilter = searchParams.get('q') ?? '';
  const fromDate = searchParams.get('from') ?? '';
  const toDate = searchParams.get('to') ?? '';
  const ageFilter = searchParams.get('age') ?? '';
  const sort = searchParams.get('sort') === 'oldest' ? 'oldest' : 'newest';
  const requestedOffset = Number.parseInt(searchParams.get('offset') ?? '0', 10);
  const offset = Number.isFinite(requestedOffset) && requestedOffset > 0 ? requestedOffset : 0;
  const [action, setAction] = useState<{ kind: 'cancel' | 'resume' | 'delete'; media: MediaListItem } | null>(null);
  const [notice, setNotice] = useState('');
  const [searchText, setSearchText] = useState(filenameFilter);
  /* ── 批次選取 ──
   * selected 只裝當頁的 id；allMatching 代表「全部符合篩選的」，這時送篩選條件
   * 給後端而不是列舉 id（2,200 筆塞不進請求，前端也只看得到當頁 40 筆）。 */
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [allMatching, setAllMatching] = useState(false);
  const [bulkAction, setBulkAction] = useState<'cancel' | 'resume' | 'delete' | null>(null);
  const clearSelection = () => { setSelected(new Set()); setAllMatching(false); };
  // 瀏覽器上一頁／下一頁也會切換篩選；不可沿用舊篩選的批次選取。
  useEffect(() => { setSelected(new Set()); setAllMatching(false); }, [statusFilter, selectedDevice, filenameFilter, fromDate, toDate, ageFilter, sort, offset]);
  useEffect(() => setSearchText(filenameFilter), [filenameFilter]);
  const { data: devices } = useQuery({ queryKey: ['devices'], queryFn: () => apiGet<Device[]>('/api/v1/devices'), refetchInterval: 10_000 });
  const { data, isLoading, isFetching, isPlaceholderData, error } = useQuery({
    queryKey: ['media', statusFilter, selectedDevice, filenameFilter, fromDate, toDate, ageFilter, sort, offset],
    queryFn: () => {
      const q = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
      if (statusFilter) q.set('status', statusFilter);
      if (selectedDevice) q.set('device', selectedDevice);
      if (filenameFilter) q.set('q', filenameFilter);
      const dates = eventDateBounds(fromDate, toDate);
      if (dates.dateFrom) q.set('date_from', dates.dateFrom);
      if (dates.dateTo) q.set('date_to', dates.dateTo);
      if (ageFilter) q.set('older_than_hours', ageFilter);
      q.set('sort', sort);
      return apiGet<MediaListResponse>(`/api/v1/media?${q}`);
    },
    placeholderData: keepPreviousData,
    refetchInterval: 10_000,
  });
  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const stats = data?.stats ?? {};
  const totalAll = Object.values(stats).reduce<number>((a, b) => a + (b ?? 0), 0);
  const activeDevice = selectedDevice || (devices?.length === 1 ? devices[0].device_id : '');
  const actionMutation = useMutation({
    mutationFn: ({ kind, media }: NonNullable<typeof action>) => kind === 'delete'
      ? apiDelete(`/api/v1/media/${media.id}`)
      : apiPost(`/api/v1/media/${media.id}/${kind}`),
    onSuccess: (_, variables) => {
      setNotice(variables.kind === 'cancel' ? '已送出取消指令，系統正在停止傳輸。' : variables.kind === 'resume' ? '已恢復等待上傳。' : '已送出刪除作業。');
      setAction(null);
      void queryClient.invalidateQueries({ queryKey: ['media'] });
      void queryClient.invalidateQueries({ queryKey: ['sync-progress'] });
    },
  });
  function updateSearch(nextValues: Record<string, string | number | undefined>) {
    const next = new URLSearchParams(searchParams);
    Object.entries(nextValues).forEach(([key, value]) => value ? next.set(key, String(value)) : next.delete(key));
    setSearchParams(next);
  }
  function filterStatus(value: string) { clearSelection(); updateSearch({ status: value, offset: 0 }); }
  function filterDevice(value: string) { clearSelection(); updateSearch({ device: value, offset: 0 }); }
  function changePage(nextOffset: number) {
    clearSelection();
    updateSearch({ offset: nextOffset });
    fileList.current?.scrollIntoView({ block: 'start' });
  }

  const pageAllSelected = rows.length > 0 && rows.every((m) => selected.has(m.id));
  const toggleRow = (id: number, on: boolean) =>
    setSelected((prev) => { const next = new Set(prev); if (on) next.add(id); else next.delete(id); return next; });

  /* 批次操作：一次請求完成。後端會跳過狀態不符的檔並回報 skipped，
   * 所以這裡不必在前端先過濾——混選也不會出錯。 */
  const bulkMutation = useMutation({
    mutationFn: (kind: 'cancel' | 'resume' | 'delete') =>
      apiPost('/api/v1/media/bulk', allMatching
        ? { action: kind, filter: { status: statusFilter || undefined, device: selectedDevice || undefined,
            q: filenameFilter || undefined, date_from: eventDateBounds(fromDate, toDate).dateFrom || undefined,
            date_to: eventDateBounds(fromDate, toDate).dateTo || undefined,
            older_than_hours: ageFilter || undefined } }
        : { action: kind, media_ids: [...selected] }),
    onSuccess: (res: unknown, kind) => {
      const r = res as { done?: number; skipped?: number };
      const verb = kind === 'cancel' ? '取消' : kind === 'resume' ? '恢復' : '刪除';
      setNotice(`已對 ${(r.done ?? 0).toLocaleString()} 個檔案送出${verb}`
        + (r.skipped ? `，${r.skipped.toLocaleString()} 個因狀態不符略過。` : '。'));
      clearSelection();
      setBulkAction(null);
      queryClient.invalidateQueries({ queryKey: ['media'] });
    },
  });

  const columns: Column<MediaListItem>[] = [
    { key: 'select', className: 'media-col-select w-9',
      header: <RowCheckbox checked={pageAllSelected}
        onChange={(on) => { setAllMatching(false); setSelected(on ? new Set(rows.map((m) => m.id)) : new Set()); }}
        label="全選本頁" />,
      cell: (m) => <RowCheckbox checked={allMatching || selected.has(m.id)}
        onChange={(on) => { setAllMatching(false); toggleRow(m.id, on); }}
        label={`選取 ${m.filename}`} /> },
    { key: 'filename', className: 'media-col-file', header: '檔案', cell: (m) => (
      <div className="min-w-0 space-y-1.5">
        <p className="truncate font-mono text-[13px] font-medium" title={m.filename}>{m.filename}</p>
        <div className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
          <button className="min-h-11 min-w-11 max-w-full truncate text-left font-mono text-primary underline-offset-4 hover:underline focus-visible:outline-primary"
            title={m.device_key}
            onClick={() => filterDevice(m.device_key)} aria-label={`篩選裝置 ${m.device_key}`}>{m.device_key}</button>
          <span>{m.site_code ?? '未指派場域'}</span><span>· {evName(m.event_type)}</span>
        </div>
      </div>
    ) },
    { key: 'status', className: 'media-col-status', header: '傳輸狀態', cell: (m) => {
      const offline = m.status === 'uploading' && devices?.find((d) => d.device_id === m.device_key)?.online === false;
      return (
      <div className="min-w-0 max-w-[240px] space-y-2 min-[1101px]:min-w-[120px]">
        <Badge variant={offline ? 'warn' : mediaVariant(m.status)}>{offline ? '裝置離線・傳輸未完成' : mediaName(m.status)}</Badge>
        {offline && <p className="text-xs text-muted-foreground">顯示最後回報的進度，等待裝置恢復。</p>}
        {m.status === 'uploading' && <FileProgress live={!offline} expected={m.expected_bytes} actual={m.actual_bytes} label={`${m.filename} 上傳進度`} />}
        {['failed', 'delete_failed', 'cancelling'].includes(m.status) && m.fail_reason && <p className="break-words text-xs text-destructive">{m.fail_reason}</p>}
      </div>
    ); } },
    { key: 'size', className: 'media-col-meta media-col-size', header: '檔案大小', cell: (m) => <span className="whitespace-nowrap font-mono text-xs">{fmtBytes(m.expected_bytes ?? m.actual_bytes)}</span> },
    { key: 'backed_up', className: 'media-col-meta media-col-backup', header: '備份', cell: (m) => m.backed_up
      ? <Badge variant="success">已備份</Badge> : <span className="text-xs text-muted-foreground">尚無備份</span> },
    { key: 'updated_at', className: 'media-col-meta media-col-updated', header: '最後更新', cell: (m) => <span className="font-mono text-xs text-muted-foreground">{fmtTime(m.updated_at)}</span> },
    { key: 'actions', className: 'media-col-actions', header: '操作', cell: (m) => {
      const canEdit = !!user && user.role !== 'viewer';
      const canCancel = canEdit && ['pending', 'uploading'].includes(m.status);
      const canResume = canEdit && m.status === 'cancelled';
      const canDelete = user?.role === 'admin' && ['stored', 'failed', 'cancelled', 'delete_failed'].includes(m.status);
      const deleteBlock = m.backed_up || m.has_backup_reference ? '已列入備份，需先處理備份引用' : m.has_dataset_reference ? '已納入資料集，需先處理資料集引用' : '';
      const busy = ['cancelling', 'deleting'].includes(m.status);
      const openAction = (kind: 'cancel' | 'resume' | 'delete') => {
        setNotice(''); actionMutation.reset(); setAction({ kind, media: m });
      };
      const secondaryDelete = canDelete && (m.status === 'stored' || canResume);
      return (
        <div className="media-row-actions flex min-w-0 flex-wrap items-center gap-2 min-[1101px]:max-w-[250px]">
          {m.status === 'stored' && <Button asChild size="sm" variant="secondary"><a href={`/api/v1/media/${m.id}/download`} aria-label={`下載 ${m.filename}`}><Download className="h-4 w-4" />下載</a></Button>}
          {canCancel && <Button size="sm" variant="ghost" onClick={() => openAction('cancel')}><Ban className="h-4 w-4" />取消</Button>}
          {canResume && <Button size="sm" variant="secondary" onClick={() => openAction('resume')}><RotateCcw className="h-4 w-4" />恢復</Button>}
          {canDelete && !secondaryDelete && (deleteBlock
            ? <span className="inline-flex min-h-11 items-center text-xs text-muted-foreground" title={deleteBlock}>{deleteBlock}</span>
            : <Button size="sm" variant="dangerSoft" onClick={() => openAction('delete')}><Trash2 className="h-4 w-4" />刪除</Button>)}
          {secondaryDelete && <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <Button size="sm" variant="ghost" className="min-w-11 px-0" aria-label={`更多 ${m.filename} 操作`}>
                <MoreHorizontal className="h-5 w-5" />
              </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content align="end" sideOffset={6} className="z-50 min-w-52 rounded-md border border-border bg-popover p-1 text-left text-popover-foreground shadow-lg">
              {deleteBlock
                ? <span className="block px-3 py-2 text-xs leading-5 text-muted-foreground" title={deleteBlock}>{deleteBlock}</span>
                : <DropdownMenu.Item className="flex min-h-11 cursor-pointer select-none items-center gap-2 rounded-sm px-3 text-sm text-destructive outline-none hover:bg-destructive/10 focus:bg-destructive/10" onSelect={() => openAction('delete')}><Trash2 className="h-4 w-4" />刪除</DropdownMenu.Item>}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>}
          {busy && <span className="inline-flex min-h-11 items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />處理中</span>}
          {!busy && m.status !== 'stored' && !canCancel && !canResume && !canDelete && <span className="inline-flex min-h-11 items-center text-xs text-muted-foreground">目前無可用操作</span>}
        </div>
      );
    } },
  ];

  const statusFilterRow = (
    <div className="grid grid-cols-2 gap-2 min-[420px]:flex min-[420px]:flex-wrap" role="group" aria-label="檔案狀態篩選">
      {STATUS_FILTERS.filter((f) => f.value === 'deleted' ? user?.role === 'admin' : !f.value || f.value === 'in_flight' || stats[f.value] || statusFilter === f.value).map((f) => <Button key={f.value} size="sm"
        className="shrink-0" variant={statusFilter === f.value ? 'default' : 'ghost'} aria-pressed={statusFilter === f.value} onClick={() => filterStatus(f.value)}>
        {f.label}{f.value !== 'deleted' && <span className="font-mono text-xs opacity-75">{f.value === 'in_flight' ? ['pending', 'uploading', 'uploaded', 'verifying'].reduce((sum, key) => sum + (stats[key] ?? 0), 0) : f.value ? stats[f.value] ?? 0 : totalAll}</span>}
      </Button>)}
    </div>
  );

  return (
    <div className="media-page space-y-3 sm:space-y-5">
      <section aria-label="檔案狀態總覽" className="reveal overflow-hidden rounded-xl border border-zinc-800 bg-sidebar text-white" style={order(0)}>
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-4 py-3 sm:px-6 sm:py-4">
          <div className="flex items-center gap-3"><FolderArchive className="h-5 w-5 text-red-400" /><h2 className="text-base font-semibold">現場影音歸檔</h2></div>
          <span className="break-all text-xs text-zinc-300">{selectedDevice || '所有裝置'} <span className="mx-2 text-zinc-600">/</span>{data ? totalAll.toLocaleString() : '—'} 個檔案</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4">
          {METRICS.map(({ key, label, Icon }) => <button key={key} onClick={() => filterStatus(key)} aria-pressed={statusFilter === key}
            className={`group min-h-20 min-w-0 border-b border-r border-white/10 p-3 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-white sm:min-h-24 sm:p-4 sm:px-5 ${statusFilter === key ? 'bg-white/10 shadow-[inset_3px_0_0_#ef4444]' : 'hover:bg-white/5'}`}>
            <div className="flex items-center justify-between gap-2 text-xs text-zinc-300"><span>{label}</span><Icon className={`h-4 w-4 ${key === 'failed' && stats.failed ? 'text-red-400' : 'text-zinc-400'}`} /></div>
            <span className="mt-1 block font-mono text-xl font-semibold tracking-tight sm:mt-3 sm:text-3xl">{data ? (stats[key] ?? 0).toLocaleString() : '—'}</span>
          </button>)}
        </div>
      </section>

      {activeDevice && <div className="reveal" style={order(1)}><SyncProgressCard key={activeDevice} deviceId={activeDevice} /></div>}

      {notice && <div role="status" className="callout rounded-md border border-success/30 bg-success/10 px-4 py-3 text-sm text-success-on">{notice}</div>}

      <Card ref={fileList} className="reveal scroll-mt-32 overflow-hidden" style={order(2)}>
        <CardHeader className="flex-wrap gap-3 border-b border-border px-4 py-4 sm:flex-row sm:items-end sm:justify-between">
          <div><CardTitle>檔案清單 <span className="ml-1 font-mono text-sm font-normal text-muted-foreground">{total}</span></CardTitle><CardDescription>{statusFilter === 'deleted' ? '已刪除紀錄僅供管理員追溯，裝置 SD 原檔未清除。' : '依裝置與傳輸狀態查找，保存後即可下載。'}</CardDescription></div>
          <label className="flex min-w-0 items-center gap-2 text-sm text-muted-foreground max-sm:w-full">
            <span className="shrink-0">裝置</span>
            <select value={selectedDevice} onChange={(e) => filterDevice(e.target.value)} className="min-h-11 min-w-0 flex-1 rounded-sm border border-border bg-card px-3 text-base text-foreground focus-visible:outline-primary sm:max-w-[240px] sm:text-sm">
              <option value="">所有裝置</option>
              {(devices ?? []).map((d) => <option key={d.device_id} value={d.device_id}>{d.label || d.device_id}</option>)}
              {selectedDevice && !devices?.some((d) => d.device_id === selectedDevice) && <option value={selectedDevice}>{selectedDevice}</option>}
            </select>
          </label>
        </CardHeader>
        <CardContent className="border-b border-border px-4 py-3">
          {/* 手機把狀態篩選放在查找表單之上：五個查找欄位會把它推出首屏，而狀態是最常用的篩選。
              平板以上表單只佔一到三列，維持原順序。兩處以 display 切換，讀取與鍵盤順序都和畫面一致。 */}
          <div className="mb-3 sm:hidden">{statusFilterRow}</div>
          <form className="mb-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-[minmax(220px,1fr)_170px_170px_150px_150px_auto]" onSubmit={(e) => { e.preventDefault(); updateSearch({ q: searchText.trim(), offset: 0 }); }}>
            <label className="min-w-0 text-xs text-muted-foreground">檔名搜尋
              <input value={searchText} onChange={(e) => setSearchText(e.target.value)} placeholder="輸入完整或部分檔名"
                className="mt-1 h-11 w-full min-w-0 rounded-sm border border-border bg-background px-3 text-base text-foreground sm:text-sm" />
            </label>
            <label className="min-w-0 text-xs text-muted-foreground">更新日期・起
              <input type="date" value={fromDate} max={toDate || undefined} onChange={(e) => updateSearch({ from: e.target.value, offset: 0 })}
                className="mt-1 h-11 w-full rounded-sm border border-border bg-background px-2 text-base text-foreground sm:text-sm" />
            </label>
            <label className="min-w-0 text-xs text-muted-foreground">更新日期・迄
              <input type="date" value={toDate} min={fromDate || undefined} onChange={(e) => updateSearch({ to: e.target.value, offset: 0 })}
                className="mt-1 h-11 w-full rounded-sm border border-border bg-background px-2 text-base text-foreground sm:text-sm" />
            </label>
            <label className="min-w-0 text-xs text-muted-foreground">等待時間
              <select value={ageFilter} onChange={(e) => updateSearch({ age: e.target.value, offset: 0 })}
                className="mt-1 h-11 w-full rounded-sm border border-border bg-background px-2 text-sm text-foreground">
                <option value="">不限</option><option value="24">超過 24 小時</option><option value="72">超過 3 天</option><option value="168">超過 7 天</option>
              </select>
            </label>
            <label className="min-w-0 text-xs text-muted-foreground">排序
              <select value={sort} onChange={(e) => updateSearch({ sort: e.target.value === 'oldest' ? 'oldest' : undefined, offset: 0 })}
                className="mt-1 h-11 w-full rounded-sm border border-border bg-background px-2 text-sm text-foreground">
                <option value="newest">最近更新</option><option value="oldest">最久待處理</option>
              </select>
            </label>
            <div className="flex items-end gap-2"><Button type="submit" size="sm">搜尋</Button>
              {(filenameFilter || fromDate || toDate || ageFilter || sort === 'oldest') && <Button type="button" size="sm" variant="ghost" onClick={() => { setSearchText(''); updateSearch({ q: undefined, from: undefined, to: undefined, age: undefined, sort: undefined, offset: 0 }); }}>清除</Button>}
            </div>
          </form>
          <div className="hidden sm:block">{statusFilterRow}</div>
        </CardContent>
        <div className="hidden min-h-14 items-center justify-between gap-3 border-b border-border bg-secondary/25 px-3 max-[1100px]:flex sm:px-4">
          <div className="flex min-h-11 items-center gap-2 text-sm font-medium">
            <RowCheckbox checked={pageAllSelected}
              onChange={(on) => { setAllMatching(false); setSelected(on ? new Set(rows.map((m) => m.id)) : new Set()); }}
              label="全選本頁" />
            <span>全選本頁</span>
          </div>
          <span className="text-xs text-muted-foreground" aria-live="polite">
            {allMatching ? `已選全部 ${total.toLocaleString()} 個` : selected.size ? `已選 ${selected.size.toLocaleString()} 個` : `本頁 ${rows.length.toLocaleString()} 個`}
          </span>
        </div>
        <div aria-busy={isFetching} className={isPlaceholderData ? 'opacity-60' : undefined}>
          <DataTable rows={rows} columns={columns} rowKey={(m) => String(m.id)} loading={isLoading} error={error}
            empty={statusFilter || selectedDevice || filenameFilter || fromDate || toDate || ageFilter ? '這個篩選條件下沒有檔案，請調整搜尋、日期、等待時間、狀態或裝置。' : '現場影音上傳後會出現在這裡。'} />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-4">
          <p className="text-xs text-muted-foreground">{rows.length ? `${offset + 1}–${offset + rows.length}` : '0'} / {total} 個檔案</p>
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" disabled={offset === 0 || isPlaceholderData} onClick={() => changePage(Math.max(0, offset - PAGE_SIZE))}><ChevronLeft className="h-4 w-4" />上一頁</Button>
            <Button size="sm" variant="secondary" disabled={offset + PAGE_SIZE >= total || isPlaceholderData} onClick={() => changePage(offset + PAGE_SIZE)}>下一頁<ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      </Card>

      <Drawer open={!!action} onOpenChange={(open) => { if (!open && !actionMutation.isPending) setAction(null); }}
        title={action?.kind === 'delete' ? '永久刪除檔案' : action?.kind === 'resume' ? '恢復上傳' : '取消上傳'}>
        {action && <div className="flex h-full flex-col">
          <div className="space-y-5">
            <div className={`grid h-12 w-12 place-items-center rounded-sm ${action.kind === 'delete' ? 'bg-destructive/10 text-destructive' : 'bg-accent text-primary'}`}>
              {action.kind === 'delete' ? <Trash2 className="h-6 w-6" /> : action.kind === 'resume' ? <RotateCcw className="h-6 w-6" /> : <Ban className="h-6 w-6" />}
            </div>
            <div>
              <p className="break-all font-mono text-sm font-semibold">{action.media.filename}</p>
              <p className="mt-2 break-all text-xs text-muted-foreground">裝置 {action.media.device_key} · {action.media.site_code ?? '未指派場域'}</p>
            </div>
            <div className="rounded-md border border-border bg-secondary/45 p-4 text-sm leading-6">
              {action.kind === 'cancel' && <p>系統會停止目前或尚未開始的傳輸，裝置 SD 卡上的原始檔仍會保留。之後可從這裡恢復上傳。</p>}
              {action.kind === 'resume' && <p>檔案會回到等待上傳狀態；裝置連線後將重新傳輸，進度會在清單中更新。</p>}
              {action.kind === 'delete' && <p><strong className="text-destructive">此操作無法復原。</strong> 平台上的檔案會永久移除，裝置 SD 卡不受影響。已納入備份或凍結資料集的檔案將由系統阻擋刪除。</p>}
            </div>
            {actionMutation.error && <p role="alert" className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">操作失敗：{actionMutation.error instanceof Error ? actionMutation.error.message : '未知錯誤'}</p>}
          </div>
          <div className="mt-auto flex flex-col-reverse gap-2 pt-8 sm:flex-row sm:justify-end">
            <Button variant="secondary" disabled={actionMutation.isPending} onClick={() => setAction(null)}>返回</Button>
            <Button variant={action.kind === 'delete' ? 'destructive' : 'default'} disabled={actionMutation.isPending}
              onClick={() => actionMutation.mutate(action)}>
              {actionMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {action.kind === 'delete' ? '確認永久刪除' : action.kind === 'resume' ? '確認恢復上傳' : '確認取消上傳'}
            </Button>
          </div>
        </div>}
      </Drawer>

      {/* 批次確認：刪除不可逆，與單筆一樣要先確認再執行 */}
      <Drawer open={!!bulkAction} onOpenChange={(open) => { if (!open && !bulkMutation.isPending) setBulkAction(null); }}
        title={bulkAction === 'delete' ? '批次永久刪除' : bulkAction === 'resume' ? '批次恢復上傳' : '批次取消上傳'}>
        {bulkAction && <div className="space-y-5">
          <p className="text-sm">
            將對 <strong className="tabular-nums">{allMatching ? total.toLocaleString() : selected.size.toLocaleString()}</strong> 個檔案執行
            {bulkAction === 'delete' ? '永久刪除' : bulkAction === 'resume' ? '恢復上傳' : '取消上傳'}。
          </p>
          <div className="rounded-md border border-border bg-secondary/45 p-4 text-sm leading-6">
            {bulkAction === 'cancel' && <p>系統會停止傳輸，裝置 SD 卡上的原始檔仍會保留，之後可從這裡恢復。狀態不符的檔案會自動略過。</p>}
            {bulkAction === 'resume' && <p>檔案會回到等待上傳狀態。每台裝置只會收到一則補傳指令，不會重複轟炸裝置。</p>}
            {bulkAction === 'delete' && <p><strong className="text-destructive">此操作無法復原。</strong> 平台上的檔案會永久移除，裝置 SD 卡不受影響。已納入備份或資料集的檔案會被自動略過。</p>}
          </div>
          {bulkMutation.error && <p role="alert" className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">操作失敗：{bulkMutation.error instanceof Error ? bulkMutation.error.message : '未知錯誤'}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" disabled={bulkMutation.isPending} onClick={() => setBulkAction(null)}>返回</Button>
            <Button variant={bulkAction === 'delete' ? 'destructive' : 'default'} disabled={bulkMutation.isPending}
              onClick={() => bulkMutation.mutate(bulkAction)}>
              {bulkMutation.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              {bulkAction === 'delete' ? '確認永久刪除' : bulkAction === 'resume' ? '確認恢復' : '確認取消'}
            </Button>
          </div>
        </div>}
      </Drawer>

      <BulkBar count={selected.size} total={total} allMatching={allMatching}
        pageAllSelected={pageAllSelected} onSelectAllMatching={() => setAllMatching(true)}
        onClear={clearSelection} busy={bulkMutation.isPending}>
        {user?.role !== 'viewer' && <>
          <Button size="sm" variant="secondary" onClick={() => { setNotice(''); bulkMutation.reset(); setBulkAction('cancel'); }}><Ban className="mr-1 h-3.5 w-3.5" />取消上傳</Button>
          <Button size="sm" variant="secondary" onClick={() => { setNotice(''); bulkMutation.reset(); setBulkAction('resume'); }}><RotateCcw className="mr-1 h-3.5 w-3.5" />恢復上傳</Button>
        </>}
        {user?.role === 'admin' && <Button size="sm" variant="dangerSoft" onClick={() => { setNotice(''); bulkMutation.reset(); setBulkAction('delete'); }}><Trash2 className="mr-1 h-3.5 w-3.5" />刪除</Button>}
      </BulkBar>
    </div>
  );
}
